import { useState, useRef, useEffect } from "react";
import { Languages, Check } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

// Language switcher — live, no page refresh. Persists to backend when authenticated.
export function LanguageSwitcher({ compact = false }) {
  const { lang, languages, setLang } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const current = languages.find((l) => l.code === lang);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg hover:bg-muted transition-colors text-foreground"
        data-testid="language-switcher-button"
        aria-label="Change language"
      >
        <Languages className="h-4 w-4 text-muted-foreground" />
        {!compact && (
          <span className="text-sm font-medium hidden sm:inline">{current?.nativeName || lang.toUpperCase()}</span>
        )}
      </button>
      {open && (
        <div
          className="absolute right-0 rtl:right-auto rtl:left-0 mt-2 w-52 max-h-80 overflow-y-auto bg-popover text-popover-foreground rounded-xl border border-border shadow-lg py-1.5 z-40"
          data-testid="language-switcher-menu"
        >
          {languages.map((l) => (
            <button
              key={l.code}
              onClick={() => { setLang(l.code); setOpen(false); }}
              className="w-full flex items-center justify-between gap-2 px-4 py-2 text-sm hover:bg-muted transition-colors"
              data-testid={`language-option-${l.code}`}
            >
              <span className="flex flex-col items-start">
                <span className="font-medium text-foreground">{l.nativeName}</span>
                <span className="text-[11px] text-muted-foreground">{l.name}</span>
              </span>
              {l.code === lang && <Check className="h-4 w-4 text-primary" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
